import {
  Animated,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Colors, Metrics, Typography } from "../../resources";
import { DataType, Orientation, SIconType } from "../../types";
import React, { useEffect, useRef, useState } from "react";

import SIcon from "../atoms/SIcon";
import SSpacer from "../atoms/SSpacer";
import { ifElse } from "../../utils";
import { noop } from "lodash";
import { useOnclickOutside } from "../../hooks";

interface IProps {
  choices: { name: string; value: string }[];
  selection?: number;
  disabled?: boolean;
  onPress?: (choice: DataType) => void;
}

const SDropDown: React.FC<IProps> = ({
  choices,
  selection = 0,
  disabled = false,
  onPress = noop,
}) => {
  const [hover, setHover] = useState<boolean>(false);
  const [pressIn, setPressIn] = useState<boolean>(false);
  const [isVisible, setIsvisible] = useState<boolean>(false);

  const [choice, setChoice] = useState<number>(selection);

  const onHoverIn = () => setHover(true);
  const onHoverOut = () => setHover(false);
  const onPressIn = () => setPressIn(true);
  const onPressOut = () => setPressIn(false);

  const angleRef = useRef<Animated.Value>(new Animated.Value(0));
  const containerRef = useRef<any>(null);

  useEffect(() => {
    setChoice(selection);
  }, [selection]);

  const _onPress = () => {
    setIsvisible((isVisible) => !isVisible);
  };

  const close = () => {
    setIsvisible(false);
  };

  useOnclickOutside(containerRef, close);

  const onSelect = (index: number) => {
    setChoice(index);
    onPress(choices[index].value as DataType);
    setIsvisible(false);
  };

  const spin = angleRef.current.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "-180deg"],
  });

  const color = ifElse(disabled, Colors.grey300, Colors.blue700);

  return (
    <View
      ref={(view) => (containerRef.current = view)}
      style={styles.container}
    >
      <Pressable
        style={[
          styles.pressable,
          hover && !disabled && styles.hover,
          pressIn && !disabled && styles.pressIn,
        ]}
        onPress={_onPress}
        disabled={disabled}
        onMouseEnter={onHoverIn}
        onMouseLeave={onHoverOut}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
      >
        <SSpacer
          type={Orientation.Horizontal}
          space={Metrics.paddingSmallStandard}
        />
        <Text style={[styles.title, { color: color }]}>
          {choices[choice].name}
        </Text>
        <Animated.View
          style={[
            styles.chevronContainer,
            {
              transform: [
                {
                  rotate: spin,
                },
              ],
            },
          ]}
        >
          <SIcon
            type={SIconType.ChevronDown}
            size={Metrics.iconSmall}
            color={Colors.blue700}
          />
          <SSpacer
            type={Orientation.Horizontal}
            space={Metrics.paddingSmallStandard}
          />
        </Animated.View>
      </Pressable>
      {isVisible ? (
        <View style={[styles.modal, { top: Metrics.size32 + 8, right: 0 }]}>
          {choices.map((ch, index) => (
            <TouchableOpacity onPress={() => onSelect(index)}>
              <Text
                style={[
                  styles.select,
                  {
                    backgroundColor:
                      index === choice ? Colors.grey000 : Colors.transparent,
                  },
                ]}
                key={ch.value}
              >
                {ch.name}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    height: Metrics.size32,
  },
  pressable: {
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    backgroundColor: Colors.transparent,
    borderRadius: Metrics.borderRadius6,
  },
  pressIn: {
    backgroundColor: Colors.midnight10,
  },
  hover: {
    backgroundColor: Colors.midnight05,
  },
  chevronContainer: {
    justifyContent: "center",
    alignItems: "center",
    margin: Metrics.padding6,
  },
  title: {
    ...Typography.body1Medium,
    color: Colors.blue700,
  },
  iconsContainer: {
    marginLeft: Metrics.padding6,
    marginRight: Metrics.padding6,
  },
  modal: {
    position: "absolute",
    justifyContent: "center",
    padding: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.grey200,
    shadowColor: "#03082e",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
    backgroundColor: Colors.white,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    height: Metrics.size48,
    width: "100%",
    backgroundColor: Colors.grey000,
    borderBottomColor: Colors.grey200,
    borderBottomWidth: 1,
  },
  select: {
    ...Typography.select,
    color: Colors.grey500,
    paddingHorizontal: 12,
    paddingVertical: 10,
    textAlign: "center",
    borderRadius: 4,
  },
  button: {
    height: Metrics.size32,
    width: Metrics.size67,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.white,
    marginRight: Metrics.padding8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.grey200,
    shadowColor: "#03082e",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  buttonText: {
    ...Typography.modalTitle,
    color: Colors.blue700,
  },
});

export default SDropDown;
